import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserAccess } from '../hooks/useUserAccess';

export const UpgradeSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userAccess, loading } = useUserAccess();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Give the webhook a moment to process
    const timer = setTimeout(() => {
      setChecking(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleContinue = () => {
    navigate('/story/new');
  };

  if (loading || checking) {
    return (
      <div className="max-w-2xl mx-auto mt-16">
        <div className="memory-stream-card text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6"></div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Processing Your Upgrade...</h2>
          <p className="text-gray-600">Please wait while we activate your new features.</p>
        </div>
      </div>
    );
  }

  const isUpgraded = userAccess?.isCollaborativeProduct;

  return (
    <div className="max-w-3xl mx-auto mt-8">
      <div className="memory-stream-card text-center">
        {isUpgraded ? (
          <>
            {/* Success State */}
            <div className="text-6xl mb-6">🎉</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Upgrade Successful!
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Welcome to Memory Stream For Two
            </p>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-purple-900 mb-4">Your New Features:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start">
                  <span className="text-green-600 mr-2 text-xl flex-shrink-0">✓</span>
                  <div>
                    <div className="font-medium text-gray-900">Two-Author Collaboration</div>
                    <div className="text-sm text-gray-600">Work together with another person</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-2 text-xl flex-shrink-0">✓</span>
                  <div>
                    <div className="font-medium text-gray-900">Shared Memories</div>
                    <div className="text-sm text-gray-600">Capture different perspectives</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-2 text-xl flex-shrink-0">✓</span>
                  <div>
                    <div className="font-medium text-gray-900">Real-time Collaboration</div>
                    <div className="text-sm text-gray-600">Answer questions together</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-2 text-xl flex-shrink-0">✓</span>
                  <div>
                    <div className="font-medium text-gray-900">All Credits Available</div>
                    <div className="text-sm text-gray-600">Use existing credits for collaborative stories</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-blue-900 mb-3">Your Updated Plan:</h3>
              <div className="space-y-2 text-blue-800">
                <div className="flex justify-between">
                  <span>Total Story Credits:</span>
                  <span className="font-medium">{userAccess.storyTokens}</span>
                </div>
                <div className="flex justify-between">
                  <span>Credits Used:</span>
                  <span className="font-medium">{userAccess.tokensUsed || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Credits Remaining:</span>
                  <span className="font-medium text-lg">{userAccess.storyTokens - (userAccess.tokensUsed || 0)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-blue-300">
                  <span>Product:</span>
                  <span className="font-medium">Memory Stream For Two ✨</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleContinue}
              className="memory-stream-button text-lg px-8 py-4"
            >
              Create Your Collaborative Story →
            </button>

            <p className="text-sm text-gray-500 mt-6">
              You can now select "Collaborative Story" when creating a new story
            </p>
          </>
        ) : (
          <>
            {/* Pending State */}
            <div className="text-5xl mb-6">⏳</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Payment Processing
            </h1>
            <p className="text-gray-700 mb-8">
              Your payment was successful! We're activating your collaborative features.
              This usually takes just a few moments.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
              <p className="text-amber-800">
                If your features don't appear within a few minutes, please refresh this page
                or contact support with your order details.
              </p>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="memory-stream-button-secondary mr-4"
            >
              Refresh Status
            </button>
            
            <button
              onClick={handleContinue}
              className="memory-stream-button"
            >
              Continue Anyway
            </button>
          </>
        )}
      </div>
    </div>
  );
};
